import { MOTION_PRESET_IDS } from '../animation/motionPresets';
import { TEMPLATE_IDS } from '../animation/procedural/spec';
import {
  ANIMATION_FPS,
  BLEND_MODES,
  CHARACTER_STYLES,
  CURRENT_SCHEMA_VERSION,
  EFFECT_RADIUS_MAX,
  EVENT_CATEGORIES,
  MAX_EFFECT_OBJECTS,
  MAX_GRADIENT_STOPS,
  OBJECT_REACTIONS,
  POSE_CHANNELS,
  SCENE_REACTIONS,
  TRANSFORM_CHANNELS,
  type Project,
} from '../domain/schemas';
import { CHARACTER_PRESET_IDS } from '../scene/characters';
import { SCENE_PRESET_IDS } from '../scene/presets';
import { VISUALIZER_PRESETS } from '../visualizer/presets';
import { OPERATION_NAMES, PACKAGE_FORMAT, PACKAGE_LIMITS, PACKAGE_VERSION } from './package';

/**
 * The Project Context (Phase 5A): the minimum an external assistant needs to write a
 * creation package for an existing project. It lists identifiers, names, geometry and timing
 * of the creative content, plus the operation catalog. It contains no binary assets, no
 * music notes, no recordings, no project name or description, and nothing else from the
 * project. `contextRevision` fingerprints the creative content, so a package written
 * against it can be refused if the project changed in the meantime.
 */

export const CONTEXT_FORMAT = 'sonicforge-project-context';

/** Fingerprint of the content packages can read or change (visual slice and asset ids). */
export function contextRevision(project: Project): string {
  const text = JSON.stringify({
    scenes: project.scenes,
    activeSceneId: project.activeSceneId,
    characters: project.characters,
    animations: project.animations,
    avSessions: project.avSessions,
    assets: project.assets.map((a) => [a.id, a.kind, a.image ?? null]),
  });
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x5bd1e995) >>> 0;
  }
  return `r${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

export function buildProjectContext(project: Project) {
  return {
    format: CONTEXT_FORMAT,
    version: 1,
    note: `Write a "${PACKAGE_FORMAT}" (version ${PACKAGE_VERSION}) for this project. Refer to the items below by their "id"; give new items a "ref" and refer to them as "@ref". Set target.projectId and target.contextRevision to the values here. See docs/ASSISTANT_CREATION_PACKAGES.md.`,
    projectId: project.id,
    contextRevision: contextRevision(project),
    schemaVersion: CURRENT_SCHEMA_VERSION,
    activeSceneId: project.activeSceneId,
    scenes: project.scenes.map((s) => ({
      id: s.id,
      name: s.name,
      width: s.width,
      height: s.height,
      background: s.background,
      gradient: s.gradient,
      backgroundImageAsset: s.backgroundImage?.assetId ?? null,
      /** Colour grade and vignette (Look v1); null when the scene has none. */
      look: s.look ? { grade: s.look.grade ?? null, vignette: s.look.vignette ?? null } : null,
      layers: s.layers.map((l) => ({ id: l.id, name: l.name, visible: l.visible, locked: l.locked, opacity: l.opacity ?? 1, blend: l.blend ?? 'normal' })),
      objects: s.objects.map((o) => ({
        id: o.id,
        name: o.name,
        type: o.type,
        layerId: o.layerId,
        x: o.x,
        y: o.y,
        width: o.width,
        height: o.height,
        rotation: o.rotation,
        opacity: o.opacity,
        locked: o.locked,
        ...('style' in o && o.style.gradient ? { gradient: o.style.gradient } : {}),
        ...(o.glow ? { glow: o.glow } : {}),
        ...(o.shadow ? { shadow: o.shadow } : {}),
        ...(o.type === 'character' ? { characterId: o.characterId } : {}),
        ...(o.type === 'image' ? { assetId: o.assetId } : {}),
      })),
    })),
    characters: project.characters.map((c) => ({
      id: c.id,
      name: c.name,
      skin: c.skin,
      head: c.head,
      eyes: c.eyes,
      hair: c.hair,
      clothing: c.clothing,
      accessories: c.accessories,
      proportions: c.proportions,
    })),
    animations: project.animations.map((a) => ({
      id: a.id,
      name: a.name,
      sceneId: a.sceneId,
      fps: a.fps,
      seconds: +(a.durationFrames / a.fps).toFixed(3),
      loop: a.loop,
      tracks: a.tracks.map((t) => ({ objectId: t.objectId, channel: t.channel, keyframes: t.keyframes.length })),
    })),
    syncSessions: project.avSessions.map((s) => ({
      id: s.id,
      name: s.name,
      sceneId: s.sceneId,
      animationId: s.animationId,
      reactions: s.mappings.map((m) => ({ id: m.id, name: m.name, reaction: m.reaction })),
      visualizerLayers: s.visualizer.layers.length,
    })),
    /** Imported image files that image objects may use (metadata only). */
    imageAssets: project.assets.filter((a) => a.kind === 'image').map((a) => ({ id: a.id, name: a.name, width: a.image?.width ?? null, height: a.image?.height ?? null })),
    catalog: {
      operations: OPERATION_NAMES,
      limits: PACKAGE_LIMITS,
      scenePresets: SCENE_PRESET_IDS,
      characterPresets: CHARACTER_PRESET_IDS,
      characterStyles: CHARACTER_STYLES,
      channels: [...TRANSFORM_CHANNELS, ...POSE_CHANNELS],
      fps: ANIMATION_FPS,
      motionPresets: MOTION_PRESET_IDS,
      motionTemplates: TEMPLATE_IDS,
      reactions: { object: OBJECT_REACTIONS, scene: SCENE_REACTIONS },
      eventCategories: EVENT_CATEGORIES,
      visualizerPresets: VISUALIZER_PRESETS.map((p) => p.id),
      /** Look v1 (package version 2): fill gradients, glow/shadow, layer blend, grade, vignette. */
      look: { blendModes: BLEND_MODES, maxGradientStops: MAX_GRADIENT_STOPS, maxEffectObjectsPerScene: MAX_EFFECT_OBJECTS, effectRadiusMax: EFFECT_RADIUS_MAX },
    },
  };
}
